import dayjs from 'dayjs'

export const FORMAT = 'ddd, MMM DD, YYYY'

/**
 * @param {Array} items - the items to be chunked
 * @param {string} datetimeField - field name of datetime field. Default is `createdAt`
 * @param {boolean} reverse - reverse the order of the chunks. Default if descending of the days
 *  @param {string} sort - sort function to be used for sorting the items in each chunk. Default is descending of the datetimeField
 */
export interface DayChunksOptions<T> {
  items: Array<T>
  datetimeField?: keyof T
  reverse?: boolean
  sort: (a: T, b: T) => number
}
export const dayChunks: <T = Record<string, any>>(
  opt: DayChunksOptions<T>
) => [{ date: string; items: Array<T> }] = ({
  items,
  datetimeField = 'createdAt' as keyof typeof items[number],
  reverse = true,
  sort,
}) => {
  const sortFunction =
    sort ||
    ((item, nextItem) => {
      return item[datetimeField] < nextItem[datetimeField] ? 1 : -1
    })
  const getDateString = (item) => dayjs(item[datetimeField]).format(FORMAT)

  // Sort items first, then extract unique dates in order (avoids format→parse round-trip)
  const sortedItems = [...items].sort(sortFunction)
  const seenDates = new Set<string>()
  const orderedDates = sortedItems
    .map((item) => getDateString(item))
    .filter((date) => {
      if (seenDates.has(date)) return false
      seenDates.add(date)
      return true
    })

  // orderedDates is now in same order as sortFunction (newest first by default)
  const chunks = orderedDates.map((date) => ({
    date,
    items: items.filter((item) => getDateString(item) === date).sort(sortFunction),
  }))

  // reverse=true (default) means descending/newest first, which chunks already is
  return (reverse ? chunks : chunks.reverse()) as [{ date: string; items: typeof items }]
}
